<template>
  <div v-if="loading" class="loading">
    <div class="loading-spinner"></div>
  </div>
  <main v-else-if="hasDetail" class="main">
    <div class="back" v-if="backVisible">
      <div class="back-content">
        <router-link :to="{ name: 'news', params: { lang: $route.params.lang } }" class="back-link">
          <span>{{ pr.back }}</span>
        </router-link>
      </div>
    </div>
    <article :class="['news-detail', { 'no-back': !backVisible }]">
      <div class="header">
        <p class="date-type">
          <span class="date-type-date">{{ detail.date }}</span>
          <Divider :space="22"/>
          <span>{{ detail.type }}</span>
        </p>
        <h1 class="title">
          <span class="epitome">{{ detail.epitome }}</span>
          {{ detail.title }}
        </h1>
        <div class="image" v-if="detail['mainPicture']">
          <img :src="detail['mainPicture']" alt="contentImg"/>
        </div>
      </div>
      <div :class="['main', { 'main-ja': !isZH }]">
        <div v-if="detail.content" class="news-content-html" v-html="cleanContent"></div>
      </div>
      <div class="done">
        <done v-if="detail.done"/>
      </div>
      <div class="footer">
        <ShareBar :detail="detail"/>
      </div>
    </article>
  </main>
</template>

<script>
import { inject } from 'vue';
import Divider from '@/components/news/Divider.vue';
import usePageResource from '@/composables/usePageResource';
import ShareBar from '@/components/news/ShareBar.vue';
import Done from '@/components/news/Done.vue';
import { LOCALES } from '@/constants/constants';

export default {
  name: 'news-detail',
  setup() {
    const { pr } = usePageResource('news');
    const locale = inject('locale');
    return {
      pr,
      locale,
    };
  },
  data() {
    return {
      loading: false,
      detail: {},
    };
  },
  components: {
    Divider,
    ShareBar,
    Done,
  },
  watch: {
    pr() {
      this.initDetail();
    },
  },
  created() {
    this.initDetail();
  },
  methods: {
    initDetail() {
      const { id } = this.$route.params;
      if (!id) {
        this.$router.replace('/');
      } else {
        const list = Array.isArray(this.pr?.list) ? this.pr.list : [];

        if (!list.length) {
          this.loading = true;
          return;
        }

        const result = list.find((item) => id === item.id);
        if (result && !result.to) {
          this.loading = false;
          this.detail = result;
        } else {
          this.loading = false;
          this.$router.replace('/');
        }
      }
    },
    processContent(html) {
      if (!html) return '';
      const root = document.createElement('div');
      root.innerHTML = html;
      root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((el) => {
        const text = (el.textContent || '').replace(/[\u00a0\u200b\u3000\s]/g, '');
        const hasMedia = el.querySelector('img, video, iframe, a');
        if (text === '' && !hasMedia) {
          el.classList.add('empty');
        }
      });
      return root.innerHTML;
    },
  },
  computed: {
    hasDetail() {
      return Boolean(this.detail?.id);
    },
    filterList() {
      if (!Array.isArray(this.pr?.list)) return [];
      return this.pr.list.filter((item) => !item.hidden);
    },
    isZH() {
      return this.locale === LOCALES.ZH;
    },
    backVisible() {
      return this.filterList.length > 5;
    },
    cleanContent() {
      return this.processContent(this.detail.content);
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

$width: 900px;
$text-color: #212121;
$md: 767px;
$height-header: 56px;

@mixin width-margin($max-width: $width) {
  max-width: $max-width;
  margin: 0 auto;
}

.loading {
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #ddd;
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

main.main {
  padding: $height-header 0 0;
}

.news-detail {
  padding: 0 20px;
  color: $text-color;
  font-size: 16px;

  &.no-back {
    padding-top: 100px;
  }

  @media only screen and (max-width: $md) {
    font-size: 14px;
  }

  ::selection {
    background-color: #cfd8dc;
  }

  ::v-deep(a) {
    color: #1565c0;
    padding: 0 0 2px 0;
    border-bottom: 1px solid #1565c0;
    text-decoration: none;
  }

  .header {
    @include width-margin;

    .date-type {
      margin-bottom: 24px;
      font-size: 18px;

      &-date {
        color: #a7aeb1;
        letter-spacing: 0.1em;
      }

      @media only screen and (max-width: $md) {
        margin-bottom: 30px;
        font-size: 14px;
      }
    }

    .title {
      padding-bottom: 28px;
      font-size: 23px;
      line-height: 36px;

      .epitome {
        display: block;
        font-size: 18px;
        line-height: 1.5;
      }

      @media only screen and (max-width: 576px) {
        padding-bottom: 24px;
        font-size: 21px;
        line-height: 30px;

        .epitome {
          font-size: 16px;
          margin-bottom: 8px;
        }
      }
    }

    .image {
      width: 100%;
      padding: 20px;
      border: 1.5px solid #ccc;
      text-align: center;
      margin-bottom: 70px;

      img {
        max-width: 100%;
      }
    }
  }

  .main {
    @include width-margin;

    &.main-ja {
      letter-spacing: 0.05em;
    }

    .news-content-html {
      width: 100%;
      line-height: 28px;

      :deep(p:not(:last-child)),
      :deep(div:not(:last-child)),
      :deep(ul:not(:last-child)),
      :deep(ol:not(:last-child)),
      :deep(blockquote:not(:last-child)),
      :deep(table:not(:last-child)) {
        margin-bottom: 30px;
      }

      :deep(img) {
        display: block;
        max-width: 100%;
        height: auto;
        margin: 0 auto 30px;
      }

      :deep(h1),
      :deep(h2),
      :deep(h3),
      :deep(h4),
      :deep(h5),
      :deep(h6) {
        font-size: 1.17em;
        margin-top: 70px;
        margin-bottom: 20px;

        &.empty {
          margin-bottom: 0;
          height: 0;
        }

        @media only screen and (max-width: $md) {
          margin-top: 40px;
        }
      }

      :deep(table) {
        width: 100%;
        height: auto !important;
        word-break: break-word;

        tr {
          border-bottom: 1px solid #aaa;
          border-color: #fff;
          height: auto !important;

          td {
            height: auto !important;
            background-color: #eceff1;
            border-right: 1px solid #aaa;
            border-color: #fff;
            padding: 20px;
            text-align: left;
            vertical-align: middle;

            &:first-child {
              white-space: nowrap;
            }
            &:last-child {
              border-right: none;
            }

            ol {
              margin-left: 20px;
            }

            ul {
              padding-left: 0;

              li {
                padding-left: 0;
                margin: 0 !important;

                &::before {
                  content: "";
                }
              }
            }
          }
        }

        @media only screen and (max-width: $md) {
          colgroup {
            display: none;
          }

          tr, td:first-child {
            border-right: none;
            padding-bottom: 0;
          }

          td {
            display: block;
          }
        }
      }

      :deep(ul) {
        list-style-position: inside;
        padding-left: 1em;

        &.custom-ul {
          list-style: none;
          padding-left: 0;

          li{
            padding-left: 1.2em;

            &:not(:last-child) {
              margin-bottom: 30px;
            }
            &::before {
              content: "-";
              margin-left: -1.2em;
              float: left;
              width: 1em;
              text-align: center;
            }
          }
        }
      }
    }

  }

  .done {
    @include width-margin;
    margin-bottom: 130px;

    @media only screen and (max-width: $md) {
      margin-bottom: 40px;
    }
  }

  .footer {
    @include width-margin;

    @media only screen and (max-width: $md) {
      padding-bottom: 80px;
    }
  }
}

.back {
  width: 100%;
  margin: 50px auto 70px;
  padding: 0 20px;

  &-content {
    @include width-margin;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding-bottom: 1em;
  }

  &-link {
    display: flex;
    align-items: center;
    text-decoration: none;

    &::before {
      content: '';
      background-image: url('../assets/icn_left.svg');
      background-repeat: no-repeat;
      width: 22px;
      height: 22px;
      -webkit-background-size: 100% 100%;
      background-size: 100% 100%;
      margin-right: 7.5px;
    }
  }

  @media screen and (max-width: $md) {
    margin: math.div($height-header, 2) auto math.div($height-header, 2);
  }
}
</style>
